import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild
} from '@headlessui/react';
import {
  FileEdit,
  RefreshCw,
  CheckCircle,
  X,
  Users,
  Building2,
  Mail,
  Phone,
  GraduationCap,
  Calendar
} from 'lucide-react';
import { useState, Fragment, useMemo } from 'react';

import { CompanyData } from '../../redux/Slices/StartTestSlice/startFulTest';
import { useAppSelector } from '../../redux/store/hooks';
import { useFetchTestSummaryQuery } from '../../services/scheduleTestApi';
import { useFetchAssociatedStudentsQuery } from '../../services/scheduleTestApi';
import { useGetAllCompanyQuery } from '../../services/startTestAPI/startTestApi';

export type Test = {
  id: string;
  assignedStudentIds: string[];
  companyId: string;
  testName: string;
  organizerId: string;
  status: 'Active' | 'Upcoming' | 'Completed';
  tagId: string;
  createdDate: string;
  testStartTime: string;
  testEndTime: string;
  tenantId: string;
};

const students = {
  data: [
    {
      id: '69297a2e3fd99306880a51ec',
      name: 'John Doe',
      email: 'john@example.com'
    },
    {
      id: '69297ba6218dbb21f2066bbf',
      name: 'Jane Smith',
      email: 'jane@example.com'
    }
  ]
};

const TestSummary = () => {
  const [testsDialogOpen, setTestsDialogOpen] = useState(false);
  const [companiesDialogOpen, setCompaniesDialogOpen] = useState(false);
  const [selectedTestType, setSelectedTestType] = useState('');
  const [selectedTests, setSelectedTests] = useState<Test[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<CompanyData[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<any[]>([]);
  const [selectedTestName, setSelectedTestName] = useState('');
  const [studentsDialogOpen, setStudentsDialogOpen] = useState(false);
  const MAX_VISIBLE = 4;
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const toggleRow = (testId: string) => {
    setExpandedRows(prev => ({
      ...prev,
      [testId]: !prev[testId]
    }));
  };

  const { associatedStudents } = useAppSelector(
    state => state.root.scheduleTest
  );

  const { data: testSummary } = useFetchTestSummaryQuery(
    {},
    {
      refetchOnMountOrArgChange: true
      // refetchOnFocus: true
    }
  );
  const { data: companies } = useGetAllCompanyQuery(null, {
    refetchOnMountOrArgChange: true
  });

  useFetchAssociatedStudentsQuery({}, { refetchOnMountOrArgChange: true });

  const activeTests = testSummary?.data?.activetests?.length || 0;
  const upcomingTests = testSummary?.data?.upcomingtests?.length || 0;
  const completedTests = testSummary?.data?.pasttests?.length || 0;

  const studentMap = useMemo(() => {
    if (!associatedStudents || associatedStudents.length === 0) return {};

    return associatedStudents.reduce((acc: Record<string, any>, s) => {
      acc[s.studentId] = s;
      return acc;
    }, {});
  }, [associatedStudents]);

  const openTestsDialog = (type: 'active' | 'upcoming' | 'completed') => {
    setSelectedTestType(type);
    let tests = [];
    if (type === 'active') tests = testSummary?.data?.activetests || [];
    else if (type === 'upcoming')
      tests = testSummary?.data?.upcomingtests || [];
    else if (type === 'completed') tests = testSummary?.data?.pasttests || [];
    setSelectedTests(tests);
    setTestsDialogOpen(true);
  };

  const openCompaniesDialog = (tests: Test[]) => {
    const companyList = tests
      .map(test =>
        companies?.data?.find((c: CompanyData) => c.id === test.companyId)
      )
      .filter(Boolean);
    const uniqueCompanies = Array.from(
      new Map(companyList?.map(item => [item?.id, item])).values()
    );
    setSelectedCompanies(uniqueCompanies);
    setCompaniesDialogOpen(true);
  };

  const openStudentsDialog = (
    assignedStudentIds: string[],
    testName: string
  ) => {
    const studentsList = assignedStudentIds
      .map(id => associatedStudents?.find(s => s.studentId === id))
      .filter(Boolean);

    setSelectedStudents(studentsList);
    setSelectedTestName(testName);
    setStudentsDialogOpen(true);
  };

  const getAssignedStudents = (assignedStudentIds: string[]) => {
    return assignedStudentIds
      .map(id => associatedStudents?.find(s => s.studentId === id))
      .filter(Boolean);
  };

  const getStudentInfo = (studentId: string) => {
    return studentMap[studentId];
  };

  const formatDate = (dateString: string) => {
    return new Date(`${dateString}z`)?.toLocaleString() ?? '-';
  };

  const getCompanyLogos = (tests: Test[], maxShow = 2) => {
    const companyList = tests
      .map(test =>
        companies?.data?.find((c: CompanyData) => c.id === test.companyId)
      )
      .filter(Boolean);

    const uniqueCompanies = Array.from(
      new Map(companyList.map(c => [c.id, c])).values()
    );

    const visible = uniqueCompanies.slice(0, maxShow);
    const remaining = uniqueCompanies.length - maxShow;

    return {
      visible,
      remaining: remaining > 0 ? remaining : 0,
      all: uniqueCompanies
    };
  };

  return (
    <div className='mx-auto max-w-7xl'>
      <div className='mb-8 grid grid-cols-1 gap-6 md:grid-cols-3'>
        {/* Active Tests Card */}
        <div className='rounded-2xl border border-blue-200 bg-white p-6 shadow-sm'>
          <div className='flex items-start gap-4'>
            <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#D4DBF9]'>
              <FileEdit className='h-6 w-6 text-blue-600' />
            </div>
            <div className='flex-1'>
              <button
                onClick={() => openTestsDialog('active')}
                className='mb-1 text-3xl font-bold text-gray-900 transition-colors hover:text-blue-600 hover:underline'
              >
                {activeTests}
              </button>
              <div className='mb-3 text-base font-semibold text-blue-600'>
                Active Tests
              </div>
              {activeTests > 0 && (
                <div className='mb-2 text-sm text-gray-600'>Companies</div>
              )}{' '}
              <div className='flex flex-wrap items-center gap-2'>
                {activeTests === 0 ? (
                  <div className='text-sm italic text-gray-400'>
                    No active tests
                  </div>
                ) : (
                  <>
                    {(() => {
                      const { visible, remaining, all } = getCompanyLogos(
                        testSummary?.data?.activetests || []
                      );
                      return (
                        <>
                          {visible.map(company => (
                            <div
                              key={company.id}
                              className='rounded-lg border border-gray-200 bg-white px-3 py-1'
                            >
                              <img
                                src={company.companyImage}
                                alt={company.companyName}
                                className='h-8 w-8 object-contain'
                                onError={e => {
                                  (e.target as HTMLImageElement).src =
                                    `https://via.placeholder.com/32x32?text=${company.companyName.charAt(
                                      0
                                    )}`;
                                }}
                              />
                            </div>
                          ))}
                          {remaining > 0 && (
                            <button
                              onClick={() =>
                                openCompaniesDialog(
                                  testSummary?.data?.activetests || []
                                )
                              }
                              className='rounded-lg border border-blue-300 bg-blue-50 px-3 py-1 text-sm font-semibold text-blue-600 transition-colors hover:bg-blue-100'
                            >
                              +{remaining}
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Upcoming Tests Card */}
        <div className='rounded-2xl border border-blue-200 bg-white p-6 shadow-sm'>
          <div className='flex items-start gap-4'>
            <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#D4DBF9]'>
              <RefreshCw className='h-6 w-6 text-blue-600' />
            </div>
            <div className='flex-1'>
              <button
                onClick={() => openTestsDialog('upcoming')}
                className='mb-1 text-3xl font-bold text-gray-900 transition-colors hover:text-blue-600 hover:underline'
              >
                {upcomingTests}
              </button>
              <div className='mb-3 text-base font-semibold text-blue-600'>
                Upcoming Tests
              </div>
              {upcomingTests > 0 && (
                <div className='mb-2 text-sm text-gray-600'>Companies</div>
              )}
              <div className='flex flex-wrap items-center gap-2'>
                {upcomingTests === 0 ? (
                  <div className='text-sm italic text-gray-400'>
                    No upcoming tests
                  </div>
                ) : (
                  <>
                    {(() => {
                      const { visible, remaining, all } = getCompanyLogos(
                        testSummary?.data?.upcomingtests || []
                      );
                      return (
                        <>
                          {visible.map(company => (
                            <div
                              key={company.id}
                              className='rounded-lg border border-gray-200 bg-white px-3 py-1'
                            >
                              <img
                                src={company.companyImage}
                                alt={company.companyName}
                                className='h-8 w-8 object-contain'
                                onError={e => {
                                  (e.target as HTMLImageElement).src =
                                    `https://via.placeholder.com/32x32?text=${company.companyName.charAt(
                                      0
                                    )}`;
                                }}
                              />
                            </div>
                          ))}
                          {remaining > 0 && (
                            <button
                              onClick={() =>
                                openCompaniesDialog(
                                  testSummary?.data?.upcomingtests || []
                                )
                              }
                              className='rounded-lg border border-blue-300 bg-blue-50 px-3 py-1 text-sm font-semibold text-blue-600 transition-colors hover:bg-blue-100'
                            >
                              +{remaining}
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Completed Tests Card */}
        <div className='rounded-2xl border border-blue-200 bg-white p-6 shadow-sm'>
          <div className='flex items-start gap-4'>
            <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#D4DBF9]'>
              <CheckCircle className='h-6 w-6 text-blue-600' />
            </div>
            <div className='flex-1'>
              <button
                onClick={() => openTestsDialog('completed')}
                className='mb-1 text-3xl font-bold text-gray-900 transition-colors hover:text-blue-600 hover:underline'
              >
                {completedTests}
              </button>
              <div className='mb-3 text-base font-semibold text-blue-600'>
                Completed Tests
              </div>
              {completedTests > 0 && (
                <div className='mb-2 text-sm text-gray-600'>Companies</div>
              )}{' '}
              <div className='flex flex-wrap items-center gap-2'>
                {completedTests === 0 ? (
                  <div className='text-sm italic text-gray-400'>
                    No completed tests
                  </div>
                ) : (
                  <>
                    {(() => {
                      const { visible, remaining, all } = getCompanyLogos(
                        testSummary?.data?.pasttests || []
                      );
                      return (
                        <>
                          {visible.map(company => (
                            <div
                              key={company.id}
                              className='rounded-lg border border-gray-200 bg-white px-3 py-1'
                            >
                              <img
                                src={company.companyImage}
                                alt={company.companyName}
                                className='h-8 w-8 object-contain'
                                onError={e => {
                                  (e.target as HTMLImageElement).src =
                                    `https://via.placeholder.com/32x32?text=${company.companyName.charAt(
                                      0
                                    )}`;
                                }}
                              />
                            </div>
                          ))}
                          {remaining > 0 && (
                            <button
                              onClick={() =>
                                openCompaniesDialog(
                                  testSummary?.data?.pasttests || []
                                )
                              }
                              className='rounded-lg border border-blue-300 bg-blue-50 px-3 py-1 text-sm font-semibold text-blue-600 transition-colors hover:bg-blue-100'
                            >
                              +{remaining}
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Tests Dialog */}
      <Transition appear show={testsDialogOpen} as={Fragment}>
        <Dialog
          as='div'
          className='relative z-50'
          onClose={() => setTestsDialogOpen(false)}
        >
          <TransitionChild
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <div className='fixed inset-0 bg-black bg-opacity-25' />
          </TransitionChild>

          <div className='fixed inset-0 overflow-y-auto'>
            <div className='flex min-h-full items-center justify-center p-4 text-center'>
              <TransitionChild
                as={Fragment}
                enter='ease-out duration-300'
                enterFrom='opacity-0 scale-95'
                enterTo='opacity-100 scale-100'
                leave='ease-in duration-200'
                leaveFrom='opacity-100 scale-100'
                leaveTo='opacity-0 scale-95'
              >
                <DialogPanel className='w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all'>
                  <div className='mb-6 flex items-center justify-between'>
                    <DialogTitle
                      as='h3'
                      className='text-2xl font-bold capitalize text-gray-900'
                    >
                      {selectedTestType} Tests
                    </DialogTitle>
                    <button
                      onClick={() => setTestsDialogOpen(false)}
                      className='text-gray-400 transition-colors hover:text-gray-600'
                    >
                      <X className='h-6 w-6' />
                    </button>
                  </div>

                  {selectedTests.length === 0 ? (
                    <div className='py-12 text-center'>
                      <div className='mb-4 flex justify-center'>
                        <div className='rounded-full bg-gray-100 p-4'>
                          <FileEdit className='h-12 w-12 text-gray-400' />
                        </div>
                      </div>
                      <h3 className='mb-2 text-lg font-semibold text-gray-900'>
                        No {selectedTestType} tests found
                      </h3>
                      <p className='text-gray-500'>
                        There are currently no {selectedTestType} tests
                        available.
                      </p>
                    </div>
                  ) : (
                    <div className='max-h-[60vh] space-y-4 overflow-y-auto'>
                      {selectedTests.map(test => {
                        const company = companies?.data?.find(
                          (c: CompanyData) => c.id === test.companyId
                        );
                        const assignedStudents = getAssignedStudents(
                          test.assignedStudentIds || []
                        );

                        return (
                          <div
                            key={test.id}
                            className='rounded-xl border border-gray-200 p-5 transition-shadow hover:shadow-md'
                          >
                            <div className='mb-4 flex items-start justify-between'>
                              <div className='flex-1'>
                                <h4 className='mb-2 text-lg font-semibold text-gray-900'>
                                  {test.testName}
                                </h4>
                                <div className='flex items-center gap-4 text-sm text-gray-600'>
                                  {/* SHOW COMPANY ONLY IF companyId EXISTS */}
                                  {test.companyId && (
                                    <div className='flex items-center gap-2'>
                                      <Building2 className='h-4 w-4' />
                                      <span>
                                        {company?.companyName ||
                                          'Unknown Company'}
                                      </span>
                                    </div>
                                  )}

                                  {/*  Students always visible */}
                                  <div className='flex items-center gap-2'>
                                    <button
                                      onClick={() =>
                                        openStudentsDialog(
                                          test.assignedStudentIds || [],
                                          test.testName
                                        )
                                      }
                                      className='flex items-center gap-2 transition-colors hover:text-blue-600'
                                    >
                                      <Users className='h-4 w-4' />
                                      <span className='mt-1 underline'>
                                        {assignedStudents?.length || 0} Students
                                      </span>
                                    </button>
                                  </div>
                                </div>
                              </div>
                              {test.companyId && company && (
                                <img
                                  src={company.companyImage}
                                  alt={company.companyName}
                                  className='h-12 w-16 flex-shrink-0 rounded-lg border border-gray-200 object-contain p-2'
                                  onError={e => {
                                    (e.target as HTMLImageElement).src =
                                      `https://via.placeholder.com/48x48?text=${company.companyName.charAt(0)}`;
                                  }}
                                />
                              )}
                            </div>

                            <div className='mb-4 grid grid-cols-2 gap-4 text-sm'>
                              <div>
                                <span className='text-gray-500'>
                                  Start Time:
                                </span>
                                <div className='font-medium text-gray-900'>
                                  {formatDate(test.testStartTime)}
                                </div>
                              </div>
                              <div>
                                <span className='text-gray-500'>End Time:</span>
                                <div className='font-medium text-gray-900'>
                                  {formatDate(test.testEndTime)}
                                </div>
                              </div>
                            </div>

                            <div>
                              <div className='mb-2 text-sm font-medium text-gray-700'>
                                Assigned Students:
                              </div>
                              {test.assignedStudentIds?.length > 0 ? (
                                <div className='flex flex-col gap-2'>
                                  {/* Students */}
                                  <div className='flex flex-wrap gap-2'>
                                    {(expandedRows[test.id]
                                      ? test.assignedStudentIds
                                      : test.assignedStudentIds.slice(
                                          0,
                                          MAX_VISIBLE
                                        )
                                    ).map(studentId => {
                                      const student = getStudentInfo(studentId);
                                      return (
                                        <div
                                          key={studentId}
                                          className='inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5'
                                        >
                                          <div className='flex h-6 w-6 items-center justify-center rounded-full bg-blue-200 text-xs font-semibold text-blue-700'>
                                            {student?.studentName?.charAt(0) ||
                                              '?'}
                                          </div>
                                          <span className='text-sm font-medium text-gray-700'>
                                            {studentMap[studentId]
                                              ?.studentName ?? 'Loading...'}
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>

                                  {/* Show More / Show Less */}
                                  {test.assignedStudentIds.length >
                                    MAX_VISIBLE && (
                                    <button
                                      className='w-fit text-sm font-medium text-blue-600 hover:underline'
                                      onClick={() => toggleRow(test.id)}
                                    >
                                      {expandedRows[test.id]
                                        ? 'Show less'
                                        : `+${test.assignedStudentIds.length - MAX_VISIBLE} more`}
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <div className='text-sm italic text-gray-400'>
                                  No students assigned
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
      {/* Companies Dialog */}
      <Transition appear show={companiesDialogOpen} as={Fragment}>
        <Dialog
          as='div'
          className='relative z-50'
          onClose={() => setCompaniesDialogOpen(false)}
        >
          <TransitionChild
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <div className='fixed inset-0 bg-black bg-opacity-25' />
          </TransitionChild>

          <div className='fixed inset-0 overflow-y-auto'>
            <div className='flex min-h-full items-center justify-center p-4 text-center'>
              <TransitionChild
                as={Fragment}
                enter='ease-out duration-300'
                enterFrom='opacity-0 scale-95'
                enterTo='opacity-100 scale-100'
                leave='ease-in duration-200'
                leaveFrom='opacity-100 scale-100'
                leaveTo='opacity-0 scale-95'
              >
                <DialogPanel className='w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all'>
                  <div className='mb-6 flex items-center justify-between'>
                    <DialogTitle
                      as='h3'
                      className='text-2xl font-bold text-gray-900'
                    >
                      All Companies
                    </DialogTitle>
                    <button
                      onClick={() => setCompaniesDialogOpen(false)}
                      className='text-gray-400 transition-colors hover:text-gray-600'
                    >
                      <X className='h-6 w-6' />
                    </button>
                  </div>

                  <div className='grid max-h-[60vh] grid-cols-2 gap-4 overflow-y-auto'>
                    {selectedCompanies?.map(company => (
                      <div
                        key={company?.id}
                        className='flex items-center gap-4 rounded-xl border border-gray-200 p-4 transition-shadow hover:shadow-md'
                      >
                        <img
                          src={company?.companyImage ?? ''}
                          alt={company?.companyName ?? ''}
                          className='h-14 w-20 rounded-lg border border-gray-200 object-contain px-2'
                          onError={e => {
                            (e.target as HTMLImageElement).src =
                              `https://via.placeholder.com/64x64?text=${company?.companyName?.charAt(0)}`;
                          }}
                        />
                        <div>
                          <h4 className='font-semibold text-gray-900'>
                            {company?.companyName}
                          </h4>
                        </div>
                      </div>
                    ))}
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
      {/* Students Dialog */}

      <Transition appear show={studentsDialogOpen} as={Fragment}>
        <Dialog
          as='div'
          className='relative z-50'
          onClose={() => setStudentsDialogOpen(false)}
        >
          <TransitionChild
            as={Fragment}
            enter='ease-out duration-200'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-150'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <div className='fixed inset-0 bg-black/30' />
          </TransitionChild>

          <div className='fixed inset-0 overflow-y-auto'>
            <div className='flex min-h-full items-center justify-center p-4'>
              <TransitionChild
                as={Fragment}
                enter='ease-out duration-200'
                enterFrom='opacity-0 scale-95'
                enterTo='opacity-100 scale-100'
                leave='ease-in duration-150'
                leaveFrom='opacity-100 scale-100'
                leaveTo='opacity-0 scale-95'
              >
                <DialogPanel className='w-full max-w-2xl rounded-2xl bg-white shadow-xl'>
                  <div className='p-6'>
                    <div className='mb-6 flex items-center justify-between'>
                      <div>
                        <h3 className='text-2xl font-bold text-gray-900'>
                          Assigned Students
                        </h3>
                        <p className='mt-1 text-sm text-gray-600'>
                          {selectedTestName}
                        </p>
                      </div>

                      <button
                        onClick={() => setStudentsDialogOpen(false)}
                        className='text-gray-400 transition-colors hover:text-gray-600'
                      >
                        <X className='h-6 w-6' />
                      </button>
                    </div>

                    {selectedStudents.length === 0 ? (
                      <div className='py-12 text-center'>
                        <div className='mb-4 flex justify-center'>
                          <div className='rounded-full bg-gray-100 p-4'>
                            <Users className='h-12 w-12 text-gray-400' />
                          </div>
                        </div>
                        <h3 className='mb-2 text-lg font-semibold text-gray-900'>
                          No students assigned
                        </h3>
                        <p className='text-gray-500'>
                          There are no students assigned to this test yet.
                        </p>
                      </div>
                    ) : (
                      <div className='max-h-[60vh] space-y-3 overflow-y-auto pr-2'>
                        {selectedStudents.map(student => (
                          <div
                            key={student.studentId}
                            className='rounded-xl border border-gray-200 p-4 transition-shadow hover:shadow-md'
                          >
                            <div className='flex items-start gap-4'>
                              <div className='flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 font-bold text-white'>
                                {student.studentName.charAt(0).toUpperCase()}
                              </div>
                              <div className='min-w-0 flex-1'>
                                <h4 className='mb-1 text-lg font-semibold text-gray-900'>
                                  {student.studentName}
                                </h4>

                                <div className='space-y-2'>
                                  <div className='flex items-center gap-2 text-sm text-gray-600'>
                                    <Mail className='h-4 w-4 flex-shrink-0' />
                                    <span className='truncate'>
                                      {student.emailId}
                                    </span>
                                  </div>

                                  <div className='flex items-center gap-2 text-sm text-gray-600'>
                                    <Phone className='h-4 w-4 flex-shrink-0' />
                                    <span>{student.mobileNumber}</span>
                                  </div>

                                  <div className='flex items-center gap-2 text-sm text-gray-600'>
                                    <GraduationCap className='h-4 w-4 flex-shrink-0' />
                                    <span>{student.branch}</span>
                                  </div>

                                  <div className='flex items-center gap-2 text-sm text-gray-600'>
                                    <Calendar className='h-4 w-4 flex-shrink-0' />
                                    <span>
                                      Year of Passing: {student.yearOfPassing}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default TestSummary;
